import { Injectable, signal } from '@angular/core';

import { TermScopeHandle } from '../models/term-scope.model';

/**
 * Root pass-through seam between a page's term-scope selector (rendered by
 * chrome OUTSIDE the page — the breadcrumb bar, ADR D1) and the page store
 * that owns the actual `term`/`appliedTerm` signals and query logic.
 *
 * Same role as the retired `DashboardRefreshService` (breadcrumb Refresh
 * button -> mounted dashboard). This service holds NO domain state of its own
 * — only a `TermScopeHandle` reference the currently-mounted page registers —
 * hence `services/`, not `state/`.
 *
 * The identity-guarded {@link unregister} is the one non-obvious contract:
 * under SSR hydration a page can be re-mounted, and the OLD instance's
 * `ngOnDestroy` may run AFTER the NEW instance has already registered. An
 * unconditional clear on unregister would blank the new registration; guarding
 * on reference identity makes a stale unregister call a no-op instead.
 */
@Injectable({ providedIn: 'root' })
export class TermScopeService {
  /** The currently-mounted page's term-scope handle, or `null` when no
      term-scope page is mounted. */
  readonly handle = signal<TermScopeHandle | null>(null);

  /** Registers the mounted page's handle — chrome reads/drives it from here on. */
  register(h: TermScopeHandle): void {
    this.handle.set(h);
  }

  /**
   * Clears the registered handle, but ONLY when `h` is still the currently
   * registered one (reference equality). A stale unregister — from an instance
   * that has already been superseded by a newer registration — is a no-op.
   */
  unregister(h: TermScopeHandle): void {
    if (this.handle() === h) {
      this.handle.set(null);
    }
  }
}
